/**
 * 新建打标任务页面
 * 从 App.jsx 搬入所有工作流状态和逻辑
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Steps, Alert, message, Button } from 'antd';
import FileUpload from '../components/FileUpload';
import ConfigurationForm from '../components/ConfigurationForm';
import ProgressBar from '../components/ProgressBar';
import ResultDownload from '../components/ResultDownload';
import ExcelViewer from '../components/ExcelViewer/ExcelViewer';
import DataAnalysisModal from '../components/DataAnalysisModal';
import {
  uploadFiles,
  startStructuredProcessing,
  getTaskStatus,
  getDownloadUrl,
} from '../services/api';
import { useUser } from '../contexts/UserContext';
import useIsMobile from '../hooks/useIsMobile';
import './NewTaskPage.css';

const { Step } = Steps;
const stepNames = ['上传文件', '选择列', '编写指令', '试标/执行', '处理中', '完成'];

const NewTaskPage = () => {
  const { refreshUser } = useUser();
  const location = useLocation();
  const isMobile = useIsMobile();

  // 状态管理 (6步: 0上传 1选列 2编写指令 3试标/执行 4处理中 5完成)
  const [currentStep, setCurrentStep] = useState(0);
  const [taskId, setTaskId] = useState(null);
  const [taskInfo, setTaskInfo] = useState({
    filename: '',
    status: 'uploaded',
    progress: 0,
    message: '',
  });
  const [error, setError] = useState(null);
  const [showExcelViewer, setShowExcelViewer] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisTaskId, setAnalysisTaskId] = useState(null);

  const pollingIntervalRef = useRef(null);

  // 上传文件成功
  const handleUploadSuccess = async (excelFile, keyFile) => {
    try {
      const response = await uploadFiles(excelFile, keyFile);
      if (response.success) {
        setTaskId(response.task_id);
        setTaskInfo({
          filename: response.filename,
          status: 'uploaded',
          progress: 0,
          message: '文件上传成功',
        });
        setCurrentStep(1);
        message.success('文件上传成功！');
      }
    } catch (err) {
      message.error(err.response?.data?.detail || '文件上传失败');
      throw err;
    }
  };

  // ConfigurationForm 内部步骤切换 (configStep 0-2 → currentStep 1-3)
  const handleConfigStepChange = (configStep) => {
    setCurrentStep(configStep + 1);
  };

  // 开始结构化打标
  const handleStructuredProcessing = async (config) => {
    try {
      const response = await startStructuredProcessing(config);
      if (response.success) {
        setCurrentStep(4);
        setTaskInfo((prev) => ({
          ...prev,
          status: 'processing',
          message: '任务已启动',
        }));
        message.success('任务已开始处理！');
        startPolling();
      }
    } catch (err) {
      message.error(err.response?.data?.detail || '启动任务失败');
      throw err;
    }
  };

  // 停止轮询
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // 轮询任务状态
  const checkStatus = useCallback(async () => {
    if (!taskId) return;
    try {
      const response = await getTaskStatus(taskId);
      if (response.success && response.task) {
        const task = response.task;
        setTaskInfo({
          filename: task.filename,
          status: task.status,
          progress: task.progress,
          message: task.message,
          estimated_cost: task.estimated_cost,
          estimated_tokens: task.estimated_tokens,
          actual_cost: task.actual_cost,
          total_input_tokens: task.total_input_tokens,
          total_output_tokens: task.total_output_tokens,
        });

        if (task.status === 'completed') {
          stopPolling();
          setCurrentStep(5);
          message.success('打标任务完成！');
          refreshUser();
          return;
        }
        if (task.status === 'failed') {
          stopPolling();
          setError(task.message || '任务处理失败');
          message.error('任务处理失败');
          return;
        }
      }
    } catch (err) {
      console.error('查询状态失败:', err);
    }
  }, [taskId, stopPolling, refreshUser]);

  // 开始轮询
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    checkStatus();
    pollingIntervalRef.current = setInterval(checkStatus, 2000);
  }, [checkStatus]);

  const handleViewExcel = () => {
    setShowExcelViewer(true);
  };

  const handleViewAnalysis = (viewTaskId) => {
    setAnalysisTaskId(viewTaskId || taskId);
    setShowAnalysisModal(true);
  };

  const handleCloseAnalysis = () => {
    setShowAnalysisModal(false);
    setAnalysisTaskId(null);
  };

  const handleRestart = () => {
    stopPolling();
    setCurrentStep(0);
    setTaskId(null);
    setShowExcelViewer(false);
    setShowAnalysisModal(false);
    setTaskInfo({
      filename: '',
      status: 'uploaded',
      progress: 0,
      message: '',
    });
    setError(null);
  };

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // 侧边栏"新建打标任务"点击时重置状态
  useEffect(() => {
    if (location.state?.reset) {
      handleRestart();
    }
  }, [location.state?.reset]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="new-task-page">
      {/* 步骤指示器 */}
      {isMobile ? (
        <div className="mobile-step-indicator">
          <div className="mobile-step-bar">
            <div className="mobile-step-bar-fill" style={{ width: `${((currentStep + 1) / 6) * 100}%` }} />
          </div>
          <span className="mobile-step-text">
            步骤 {currentStep + 1}/6 · {stepNames[currentStep]}
          </span>
        </div>
      ) : (
        <div style={{
          padding: '20px 24px',
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-sm)',
          marginBottom: 24,
        }}>
          <Steps current={currentStep}>
            <Step title="上传文件" />
            <Step title="选择列" />
            <Step title="编写指令" />
            <Step title="试标/执行" />
            <Step title="处理中" />
            <Step title="完成" />
          </Steps>
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <Alert
          message="处理失败"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
          action={<Button type="link" onClick={handleRestart}>重新开始</Button>}
        />
      )}

      {/* 步骤0: 文件上传 */}
      {currentStep === 0 && (
        <FileUpload onUploadSuccess={handleUploadSuccess} />
      )}

      {/* 步骤1-3: 配置打标任务 (configStep 0=选列, 1=编写指令, 2=试标/执行) */}
      {currentStep >= 1 && currentStep <= 3 && (
        <ConfigurationForm
          taskId={taskId}
          filename={taskInfo.filename}
          onSubmit={handleStructuredProcessing}
          configStep={currentStep - 1}
          onStepChange={handleConfigStepChange}
        />
      )}

      {/* 步骤4: 处理中 */}
      {currentStep === 4 && (
        <ProgressBar
          progress={taskInfo.progress}
          message={taskInfo.message}
          status={taskInfo.status}
        />
      )}

      {/* 步骤5: 完成 */}
      {currentStep === 5 && (
        <ResultDownload
          taskId={taskId}
          filename={taskInfo.filename}
          taskInfo={taskInfo}
          onDownload={getDownloadUrl}
          onRestart={handleRestart}
          onViewExcel={handleViewExcel}
          onViewAnalysis={handleViewAnalysis}
        />
      )}

      {/* Excel查看器 */}
      {currentStep === 5 && showExcelViewer && (
        <ExcelViewer taskId={taskId} filename={taskInfo.filename} />
      )}

      {/* 数据分析弹窗 */}
      <DataAnalysisModal
        visible={showAnalysisModal}
        onClose={handleCloseAnalysis}
        taskId={analysisTaskId || taskId}
      />
    </div>
  );
};

export default NewTaskPage;
